import React, { useState, useEffect, useRef } from "react"
import { useHistory, useRouteMatch } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import DealerRecallPanelComponent from "./dealerRecallPanel.component"
import moment from "moment";
import { getDealerRecallList, resetDealerRecallListing } from "modules/stockflow/redux";
import { selectLoading } from "modules/notification";
import { generatePaginationOptions, updateUrlQueryParam } from "lib/helper";
import {AccessRights} from "lib/constants/accessRights";
import {canAccess} from "../../../../profile/redux/selector/profile.selector";

function DealerRecallPanelContainer() {
  const dispatch = useDispatch()
  const match = useRouteMatch()
  const history = useHistory()
  const id = match.params.dealerId

  const lang = useSelector(state => state.constant.languages)
  const isFetching = useSelector(state => selectLoading(state, getDealerRecallList.typePrefix))
  const totalFiltered = useSelector(state => state.stockflowDealer.recallList.totalFiltered)
  const totalRecords = useSelector(state => state.stockflowDealer.recallList.totalRecords)
  const recallList = useSelector(state => state.stockflowDealer.recallList.list)
  const isError = useSelector(state => state.stockflowDealer.recallList.isError)
  const hasStockflowRecallViewAccessRight = useSelector(state =>
    canAccess(state, AccessRights.TrackcoPortal.RECALL_READ));
  const hasStockflowDealerViewAccessRight = useSelector(state =>
    canAccess(state, AccessRights.TrackcoPortal.DEALER_READ));

  const isSearchResult = recallList.length < totalRecords
  const [paginationOptions, setPaginationOptions] = useState([]);
  const [isFilterUpdated, setIsFilterUpdated] = useState(false)
  const [tableFilter, setTableFilter] = useState({
    page: 0,
    rowsPerPage: 25,
    order: "desc",
    orderBy: "created_at",
    search: "",
    fromDate: moment().subtract(7, "days").format("YYYY-MM-DD"),
    toDate: moment().format("YYYY-MM-DD"),
    recallFrom: [],
  })

  const tableFilterInitialRef = useRef(true)

  useEffect(() => {
    return () => {
      dispatch(resetDealerRecallListing())
    }
  }, [dispatch])

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const isFilter = searchParams.has("is_filter")
    let filter = sessionStorage.getItem(`dealer_recall_filter_${id}`);

    let temp = {...tableFilter}
    if (filter) {
      temp = JSON.parse(filter)
      setIsFilterUpdated(true)
    }

    if (isFilter) {
      updateUrlQueryParam(null, ["is_filter"])
    }

    setTableFilter(temp)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!tableFilterInitialRef.current) {
      getData()
    }
    tableFilterInitialRef.current = false
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableFilter])

  useEffect(() => {
    setPaginationOptions(generatePaginationOptions(totalFiltered))
  }, [totalFiltered])

  const getData = () => {
    dispatch(getDealerRecallList({
      id,
      length: tableFilter.rowsPerPage,
      start: tableFilter.rowsPerPage * tableFilter.page,
      order: tableFilter.order,
      order_by: tableFilter.orderBy,
      filter: {
        search: tableFilter.search,
        from_date: tableFilter.fromDate,
        to_date: tableFilter.toDate,
        recall_from: tableFilter.recallFrom,
        respond_date: tableFilter.respondDate,
      }
    }))
  }

  const handleChangePage = (event, newPage) => {
    let newData = {
      ...tableFilter,
      page: newPage,
    }
    setIsFilterUpdated(true)
    setTableFilter(newData)
  }

  const handleChangeRowsPerPage = event => {
    let newData = {
      ...tableFilter,
      page: 0,
      rowsPerPage: event.target.value,
    }
    setIsFilterUpdated(true)
    setTableFilter(newData)
  }

  const handleSearch = (filter) => {
    const { search, dateRange, recallFrom } = filter

    let newData = {
      ...tableFilter,
      page: 0,
      search: search,
      fromDate: dateRange?.from
        ? moment(dateRange.from).format("YYYY-MM-DD")
        : "",
      toDate: dateRange?.to ? moment(dateRange.to).format("YYYY-MM-DD") : "",
      recallFrom: recallFrom,
    }

    setIsFilterUpdated(true)
    setTableFilter(newData)
  }

  const clearSearch = () => {
    let newData = {
      ...tableFilter,
      page: 0,
      search: "",
      fromDate: moment().subtract(7, "days").format("YYYY-MM-DD"),
      toDate: moment().format("YYYY-MM-DD"),
      recallFrom: [],
    }
    setIsFilterUpdated(false)
    setTableFilter(newData)
    sessionStorage.removeItem(`dealer_recall_filter_${id}`)
  }

  const changeOrder = (orderBy, order) => {
    let newData = {
      ...tableFilter,
      order,
      orderBy
    }
    setIsFilterUpdated(true)
    setTableFilter(newData)
  }

  const handleViewDetail = (url) => {
    if (isFilterUpdated) {
      updateUrlQueryParam({ is_filter: true })
      sessionStorage.setItem(`dealer_recall_filter_${id}`, JSON.stringify(tableFilter));
    }
    history.push(url)
  }

  return (
    <DealerRecallPanelComponent 
      hasStockflowRecallViewAccessRight={hasStockflowRecallViewAccessRight}
      hasStockflowDealerViewAccessRight={hasStockflowDealerViewAccessRight}
      id={id}
      recalls={recallList}
      isFetching={isFetching}
      isError={isError}
      handleChangePage={handleChangePage}
      handleChangeRowsPerPage={handleChangeRowsPerPage}
      page={tableFilter.page}
      rowsPerPage={tableFilter.rowsPerPage}
      totalFiltered={totalFiltered}
      handleSearch={handleSearch}
      clearSearch={clearSearch}
      handleReload={getData}
      isSearchResult={isSearchResult}
      paginationOptions={paginationOptions}
      changeOrder={changeOrder}
      lang={lang}
      tableFilter={tableFilter}
      handleViewDetail={handleViewDetail}
    />
  )
}

export default DealerRecallPanelContainer
